import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrashIcon } from '@heroicons/react/24/outline';
import DeleteConfirm from '../DeleteConfirm';
import { toFriendlyTime } from '../../services/utils';
import { CardPreview } from './CardPreview';

/**
 * The owner's dashboards as a card grid. Presentational: it owns the
 * confirmation dialog and nothing else, so the page keeps one story about
 * what the list is.
 */
export function DashboardList({ items, onDelete }) {
  const [target, setTarget] = useState(null);

  const confirm = () => {
    const slug = target.id;
    setTarget(null);
    onDelete(slug);
  };

  return (
    <>
      <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((d) => (
          <DashboardCard key={d.id} dashboard={d} onDelete={() => setTarget(d)} />
        ))}
      </ul>

      <DeleteConfirm
        isOpen={target !== null}
        closeModal={() => setTarget(null)}
        onConfirm={confirm}
        message={
          target
            ? `Delete "${target.title}"? You can ask your client to build another.`
            : ''
        }
      />
    </>
  );
}

function DashboardCard({ dashboard: d, onDelete }) {
  const deviceCount = d.devices?.length ?? 0;

  return (
    <li className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white transition hover:border-gray-300 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700">
      <CardPreview seed={d.id} />

      <div className="border-t border-gray-200 px-6 py-5 dark:border-gray-800">
        <div className="flex items-center gap-2">
          {/* The link's ::after covers the whole card, so the card is one
              target without nesting the delete button inside an <a>. A
              dashboard is a standalone page, so it opens in its own tab. */}
          <Link
            to={`/dashboards/${encodeURIComponent(d.id)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-lg font-semibold text-gray-900 after:absolute after:inset-0 focus:outline-none focus-visible:after:rounded-2xl focus-visible:after:ring-2 focus-visible:after:ring-blue-600 dark:text-gray-50"
          >
            {d.title}
          </Link>
          {d.seeded && (
            <span className="shrink-0 rounded-full bg-blue-50 px-2.5 py-0.5 text-sm text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
              Example
            </span>
          )}
        </div>
        <p className="mt-1 text-gray-500 dark:text-gray-400">
          {d.seeded
            ? 'Created by Zafron from your readings'
            : `${deviceCount} ${deviceCount === 1 ? 'device' : 'devices'} · updated ${toFriendlyTime(d.updatedAt)}`}
        </p>
      </div>

      <button
        type="button"
        onClick={onDelete}
        aria-label={`Delete ${d.title}`}
        className="absolute right-3 top-3 z-10 rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 opacity-0 shadow-sm transition hover:text-red-600 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 group-hover:opacity-100 [@media(hover:none)]:opacity-100 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400"
      >
        <TrashIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </li>
  );
}
